import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Controller, FormProvider, useFieldArray, useForm, useFormContext } from 'react-hook-form'
import { CloseButton, Col, Container, Modal as BootstrapModal, Row, Spinner } from 'react-bootstrap'
import * as Sentry from '@sentry/react'

import { selectRoomSession, selectRoomSessionMeetingTitle } from '../../../store/meetingSummary/roomSession'
import { selectAccountId, selectSafeAuthenticatedUser } from '../../../store/auth/authSlice'
import { useGetUsersQuery, useLazyGetUsersQuery } from '../../../store/user/user'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { useCreateIntegrationEmailMutation, useFilterEmailThreadsMutation } from '../../../store/integrations/integrations'

import { IntegrationServiceId } from '../../../common/integrations'
import { HTML_TYPE } from '../../../common/copy'

import { SelectDropdown, SelectOption } from '../../../components/SelectDropdown/SelectDropdown'
import { UserTagDropdownItemOption } from '../../../components/SelectDropdown/UserTagDropdownItemOption'
import { Button } from '../../../components/Button/Button'
import { TagElement } from '../../../components/Tag/Tag'
import { Modal } from '../../../components/Modal/Modal'

import { AiResult, StructuredSummaryResult } from '../../../models/AiResult'
import { AiFeature } from '../../../models/AiFeature'
import { User } from '../../../models/User'

import { copyFullStructuredSummaryResult } from '../../../apps/professional-app/fa/MeetingSummary/copy'
import { FormFeedback } from '../../../components/Form/FormFeedback'
import { selectCurrentUser } from '../../../store/meetingSummary/user'
import { showToast } from '../../../store/meetingSummary/display'
import { IntegrationEmail, IntegrationEmailIdentity } from '../../../models/Email'
import { SplitButton } from '../../../components/SplitButton/SplitButton'
import { getUserDisplayName } from '../../../apps/professional-app/fa/common/aifeatures/utils'
import { SendEmailConfirmationModal } from '../../EmailV2/EmailResponse/SendEmailConfirmationModal'
import { useExportConfirmation } from '../../../components/ConfirmationDialog/useExportConfirmation'
import { TrackEvent, useTracking } from '../../../apps/professional-app/fa/common/tracking'
import { getAIFeatureAndResultTrackingDetails } from '../../../apps/professional-app/fa/tracking'

type FormValues = {
  recipients: IntegrationEmailIdentity[]
  ccRecipients: IntegrationEmailIdentity[]
  selectedIntegrationEmail: IntegrationEmail | null
}

interface AddResultToEmailProviderModalProps {
  aiFeature: AiFeature
  aiResult: AiResult
  serviceId: string
  show: boolean
  onHide: () => void
}

const mapUserToOption = (user: User): SelectOption => {
  return {
    id: user.id,
    label: `${user.name} (${user.email})`,
  }
}

const mapIntegrationEmailToOption = (integrationEmail: IntegrationEmail): SelectOption => {
  return {
    id: integrationEmail.externalId,
    label: integrationEmail.subject || '(no subject)',
  }
}

export const AddResultToEmailProviderModal: React.FC<AddResultToEmailProviderModalProps> = ({ show, onHide, aiFeature, aiResult, serviceId }) => {
  const dispatch = useAppDispatch()

  const { tracked } = useTracking()

  const accountId = useAppSelector(selectAccountId)
  const { id: currentUserId, integrations: currentUserIntegrations } = useAppSelector(selectCurrentUser) ?? {}

  const roomSession = useAppSelector(selectRoomSession)

  const roomSessionTitle = useAppSelector(selectRoomSessionMeetingTitle)

  const { runMutationWithConfirmation, ConfirmationModal, accountIsLoading } = useExportConfirmation({ roomSession })

  const [showConfirmationModal, setShowConfirmationModal] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [sendIsLoading, setSendIsLoading] = useState<boolean>(false)
  const [isSending, setIsSending] = useState<boolean>(false)
  const [saveAsDraftIsLoading, setSaveAsDraftIsLoading] = useState<boolean>(false)

  const [triggerCreateIntegrationEmail, { isSuccess }] = useCreateIntegrationEmailMutation()

  const handleSave = async (data: FormValues, isDraft: boolean) => {
    if (isDraft) {
      setSaveAsDraftIsLoading(true)
    } else {
      setSendIsLoading(true)
    }
    try {
      const htmlBody = copyFullStructuredSummaryResult(aiFeature, aiResult.result as StructuredSummaryResult)[HTML_TYPE]
      const body = {
        ...(data.selectedIntegrationEmail && { inReplyTo: data.selectedIntegrationEmail.externalId }),
        subject: `${roomSessionTitle} - Follow-Up`,
        body: htmlBody,
        recipients: data.recipients.map(({ email }) => ({ email })),
        ccRecipients: data.ccRecipients.map(({ email }) => ({ email })),
        isDraft,
      }

      const { data: integrationEmail } = await triggerCreateIntegrationEmail({
        accountId,
        connector: serviceId,
        body,
      })

      if (!integrationEmail) {
        return
      }

      const integration = currentUserIntegrations?.find((e) => e.serviceId === serviceId)
      if (integration?.serviceId !== IntegrationServiceId.GMAIL) {
        return
      }

      window.open(`https://mail.google.com/mail/u/${integration.externalId}/#drafts?compose=${integrationEmail.externalId}`, '_blank')
    } catch (e) {
      console.error(e)
      Sentry.captureException(e)
    } finally {
      setSaveAsDraftIsLoading(false)
      setSendIsLoading(false)
      setShowConfirmationModal(false)
    }
  }

  const _handleSaveAsDraft = (data: FormValues) => handleSave(data, true)

  const handleSaveAsDraft = tracked(_handleSaveAsDraft, {
    eventName: TrackEvent.AI_RESULT_PUSHED_AS_DRAFT_TO_EMAIL_PROVIDER,
    eventDetails: { connector: serviceId, ...getAIFeatureAndResultTrackingDetails(aiFeature, aiResult) },
  })

  const handleSaveAsDraftWithConfirmation = async (data: FormValues) => {
    await runMutationWithConfirmation(async () => {
      await handleSaveAsDraft(data)
    })
  }

  const _handleSend = (data: FormValues) => handleSave(data, false)

  const handleSend = tracked(_handleSend, {
    eventName: TrackEvent.AI_RESULT_SENT_TO_EMAIL_PROVIDER,
    eventDetails: { connector: serviceId, ...getAIFeatureAndResultTrackingDetails(aiFeature, aiResult) },
  })

  const handleSendWithConfirmation = async (data: FormValues) => {
    setIsSending(true)
    try {
      await runMutationWithConfirmation(async () => {
        await handleSend(data)
      })
    } finally {
      setIsSending(false)
    }
  }

  const form = useForm<FormValues>({ defaultValues: { recipients: [], ccRecipients: [] }, mode: 'onChange' })

  useEffect(() => {
    if (!roomSession) {
      return
    }
    const recipientUsers = roomSession.users.filter((user) => user.roles.some((role) => role.name === 'Client') && user.email)
    const ccRecipientUsers = roomSession.users.filter((user) => user.roles.every((role) => role.name !== 'Client') && user.id !== currentUserId && user.email)
    form.setValue('selectedIntegrationEmail', null)
    form.setValue(
      'recipients',
      recipientUsers.map((e) => ({ email: e.email, name: getUserDisplayName(e) })),
    )
    form.setValue(
      'ccRecipients',
      ccRecipientUsers.map((e) => ({ email: e.email, name: getUserDisplayName(e) })),
    )
    form.clearErrors()
    setIsLoading(false)
  }, [form, currentUserId, roomSession])

  useEffect(() => {
    if (isSuccess) {
      onHide()
      form.reset()
      dispatch(
        showToast({
          variant: 'success',
          icon: 'bi-check-circle-fill',
          message: 'Results were successfully saved to your inbox.',
        }),
      )
    }
  }, [form, isSuccess, onHide, dispatch])

  return (
    <>
      <Modal
        show={show}
        onHide={onHide}
        header={
          <BootstrapModal.Title className='w-100 d-flex align-items-center justify-content-center'>
            Save to&nbsp;<span className='text-capitalize'>{serviceId}</span>
          </BootstrapModal.Title>
        }
        body={
          <>
            <FormProvider {...form}>
              {showConfirmationModal ? (
                <SendEmailConfirmationModal
                  show={showConfirmationModal}
                  isSending={isSending}
                  onConfirm={form.handleSubmit(handleSendWithConfirmation)}
                  onHide={() => setShowConfirmationModal(false)}
                />
              ) : null}
              {roomSession && !isLoading ? (
                <AddResultToEmailProviderModalBody serviceId={serviceId} />
              ) : (
                <div className='d-flex justify-content-center'>
                  <Spinner />
                </div>
              )}
            </FormProvider>
          </>
        }
        footer={
          <div className='d-flex gap-3'>
            <Button variant='secondary' onClick={onHide}>
              Cancel
            </Button>
            <SplitButton
              variant='primary'
              label='Save as draft'
              onClick={form.handleSubmit(handleSaveAsDraftWithConfirmation)}
              loading={saveAsDraftIsLoading}
              disabled={saveAsDraftIsLoading || sendIsLoading || !form.formState.isValid || accountIsLoading}
            >
              <Button
                className='px-2 py-1 flex-grow-1 justify-content-start'
                variant='primary'
                title='Send'
                onClick={() => setShowConfirmationModal(true)}
                loading={sendIsLoading}
                disabled={sendIsLoading || saveAsDraftIsLoading || !form.formState.isValid || accountIsLoading}
              >
                Send
              </Button>
            </SplitButton>
          </div>
        }
        centered={true}
      />
      <ConfirmationModal />
    </>
  )
}

interface AddResultToEmailProviderModalBodyProps {
  serviceId: string
}

const AddResultToEmailProviderModalBody: React.FC<AddResultToEmailProviderModalBodyProps> = ({ serviceId }) => {
  const accountId = useAppSelector(selectAccountId)
  const currentUser = useAppSelector(selectSafeAuthenticatedUser)

  const {
    control,
    watch,
    reset,
    formState: { errors },
  } = useFormContext<FormValues>()
  const selectedIntegrationEmail = watch('selectedIntegrationEmail')

  const {
    fields: recipients,
    append: addRecipient,
    remove: removeRecipient,
  } = useFieldArray({
    control,
    name: 'recipients',
    rules: {
      validate: (value, formValues) => {
        if (!value?.length) {
          return 'At least one recipient must be selected.'
        }
        const { selectedIntegrationEmail } = formValues
        if (selectedIntegrationEmail && !value.map((e) => e.email).includes(selectedIntegrationEmail.sender.email)) {
          return 'Recipients must include original sender.'
        }
      },
    },
  })

  const {
    fields: ccRecipients,
    append: addCcRecipient,
    remove: removeCcRecipient,
  } = useFieldArray({
    control,
    name: 'ccRecipients',
  })

  const sentFromFilter = useMemo(() => recipients.map((e) => e.email), [recipients])

  const [triggerFilterEmailThreads, { isLoading: isIntegrationEmailsFetching }] = useFilterEmailThreadsMutation()

  const { data: usersResponse } = useGetUsersQuery({
    accountId: accountId,
    identityTypes: ['idp', 'external'],
    pageRequest: { page: 0, size: 100 },
  })
  const [triggerGetUsers] = useLazyGetUsersQuery()

  const [integrationEmails, setIntegrationEmails] = useState<IntegrationEmail[]>([])
  const [integrationEmailsOptions, setIntegrationEmailsOptions] = useState<SelectOption[]>([])
  const [recipientUsers, setRecipientUsers] = useState<User[]>([])
  const [recipientUserOptions, setRecipientUserOptions] = useState<SelectOption[]>([])
  const [ccRecipientUsers, setCcRecipientUsers] = useState<User[]>([])
  const [ccRecipientUserOptions, setCcRecipientUserOptions] = useState<SelectOption[]>([])

  useEffect(() => {
    const fetchEmailThreads = async () => {
      if (!sentFromFilter.length) {
        return
      }
      try {
        const emailThreads = await triggerFilterEmailThreads({
          accountId,
          emailConnector: serviceId,
          filters: {
            filter: { senders: sentFromFilter },
          },
        }).unwrap()
        if (!emailThreads.data) {
          return
        }
        setIntegrationEmails(
          emailThreads.data
            .flatMap((thread) => thread.emails)
            .filter((email) => sentFromFilter.some((sentFrom) => email.sender.email.toLowerCase() === sentFrom.toLowerCase())),
        )
      } catch (e) {
        console.error(e)
      }
    }
    fetchEmailThreads()
  }, [accountId, serviceId, sentFromFilter, triggerFilterEmailThreads])

  useEffect(() => {
    if (!usersResponse) {
      return
    }
    setRecipientUsers(usersResponse.data.filter((user) => !!user.email && user.id !== currentUser.id))
    setCcRecipientUsers(usersResponse.data.filter((user) => !!user.email && user.id !== currentUser.id))
  }, [usersResponse, currentUser])

  useEffect(() => {
    setIntegrationEmailsOptions(integrationEmails.map(mapIntegrationEmailToOption))
  }, [integrationEmails])

  useEffect(() => {
    setRecipientUserOptions(recipientUsers.map(mapUserToOption))
  }, [recipientUsers])

  useEffect(() => {
    setCcRecipientUserOptions(ccRecipientUsers.map(mapUserToOption))
  }, [ccRecipientUsers])

  const handleRecipientSearchInputChange = async (searchInput: string, setUsers: (users: User[]) => void) => {
    try {
      const response = await triggerGetUsers({
        accountId,
        name: searchInput,
        identityTypes: ['idp', 'external'],
        pageRequest: { size: 100, page: 0 },
      }).unwrap()
      if (!response?.data) {
        return
      }
      setUsers(response.data.filter((user) => !!user.email && user.id !== currentUser.id))
    } catch (e) {
      console.error(e)
    }
  }

  const onRecipientSearchInputChange = async (searchInput: string) => {
    await handleRecipientSearchInputChange(searchInput, setRecipientUsers)
  }

  const onCcRecipientSearchInputChange = async (searchInput: string) => {
    await handleRecipientSearchInputChange(searchInput, setCcRecipientUsers)
  }

  const onIntegrationEmailOptionSelected = useCallback(
    (option: SelectOption) => {
      const integrationEmail = integrationEmails.find((e) => e.externalId === option.id)
      if (!integrationEmail) {
        return
      }

      const fullRecipients = [...recipients, integrationEmail.sender]
      const fullCcRecipients = [...ccRecipients, ...integrationEmail.ccRecipients]

      const newRecipientExternalIds = [...new Set(fullRecipients.map((e) => e.email))]
      const newCcRecipientExternalIds = [...new Set(fullCcRecipients.map((e) => e.email))]

      const newRecipients = newRecipientExternalIds.map((email) => fullRecipients.find((e) => e.email.toLowerCase() === email.toLowerCase()))
      const newCcRecipients = newCcRecipientExternalIds.map((email) => fullCcRecipients.find((e) => e.email.toLowerCase() === email.toLowerCase()))

      reset({
        selectedIntegrationEmail: integrationEmail,
        recipients: newRecipients,
        ccRecipients: newCcRecipients,
      })
    },
    [recipients, ccRecipients, integrationEmails, reset],
  )

  return (
    <Container className='d-flex flex-column gap-4'>
      <Row>
        <Col xs={12} className='p-0 d-flex flex-column gap-1'>
          <span className='lead-extra-small'>Recipients</span>
          <RecipientSelector
            users={recipientUsers}
            options={recipientUserOptions}
            selectedRecipients={recipients}
            addRecipient={addRecipient}
            removeRecipient={removeRecipient}
            onSearchInputChange={onRecipientSearchInputChange}
            disabled={serviceId === IntegrationServiceId.OUTLOOK_MAIL && !!selectedIntegrationEmail}
            error={errors.recipients?.root?.message}
          />
        </Col>
      </Row>
      <Row>
        <Col xs={12} className='p-0 d-flex flex-column gap-1'>
          <span className='lead-extra-small'>Recipients (Cc)</span>
          <RecipientSelector
            users={ccRecipientUsers}
            options={ccRecipientUserOptions}
            selectedRecipients={ccRecipients}
            addRecipient={addCcRecipient}
            removeRecipient={removeCcRecipient}
            onSearchInputChange={onCcRecipientSearchInputChange}
            disabled={serviceId === IntegrationServiceId.OUTLOOK_MAIL && !!selectedIntegrationEmail}
          />
        </Col>
      </Row>
      <Row>
        <Col xs={12} className='p-0 d-flex flex-column gap-1'>
          <span className='lead-extra-small'>Create as reply (Optional)</span>
          <Controller
            name='selectedIntegrationEmail'
            control={control}
            render={({ field }) => (
              <SelectDropdown
                initialSelectedOption={null}
                className='py-2 zocks-px-0.75'
                placeholder='Select your e-mail'
                options={integrationEmailsOptions}
                loading={isIntegrationEmailsFetching}
                onOptionSelected={onIntegrationEmailOptionSelected}
                // onSearchInputChange={onIntegrationEmailSearchInputChange}
                // searchable={true}
                showDeselectOption={true}
                deselectOptionLabel={'Select your e-mail'}
                onDeselect={() => field.onChange(null)}
              />
            )}
          />
        </Col>
      </Row>
    </Container>
  )
}

interface RecipientSelectorProps {
  users: User[]
  options: SelectOption[]
  selectedRecipients: IntegrationEmailIdentity[]
  onSearchInputChange: (searchInput: string) => Promise<void>
  addRecipient: (recipient: IntegrationEmailIdentity) => void
  removeRecipient: (index: number) => void
  disabled?: boolean
  error?: string
}

const RecipientSelector: React.FC<RecipientSelectorProps> = ({
  users,
  options,
  selectedRecipients,
  onSearchInputChange,
  addRecipient,
  removeRecipient,
  disabled = false,
  error,
}) => {
  const handleOptionSelected = (option: SelectOption) => {
    const user = users.find((e) => e.id === option.id)
    if (!user) {
      return
    }
    const existingRecipient = selectedRecipients.find((e) => e.email.toLowerCase() === user.email.toLowerCase())
    if (existingRecipient) {
      return
    }
    addRecipient({ email: user.email, name: getUserDisplayName(user) })
  }

  const handleSearchInputSelected = (searchInput: string) => {
    const existingRecipient = selectedRecipients.find((e) => e.email.toLowerCase() === searchInput.toLowerCase())
    if (existingRecipient) {
      return
    }
    addRecipient({ email: searchInput })
  }

  return (
    <>
      <SelectDropdown
        options={options}
        initialSelectedOption={null}
        onSearchInputChange={onSearchInputChange}
        onOptionSelected={handleOptionSelected}
        onSearchInputSelected={handleSearchInputSelected}
        DropDownItemOptionComponent={UserTagDropdownItemOption}
        clearDropdownOnOptionSelected={true}
        searchable={true}
        disabled={disabled}
        showSearchInputSelectButton={true}
      />
      {error && <FormFeedback>{error}</FormFeedback>}
      {selectedRecipients.length ? (
        <Row>
          <Col className='d-flex flex-wrap gap-1'>
            {selectedRecipients.map((identity, index) => (
              <TagElement key={identity.email}>
                {identity.name ? `${identity.name} (${identity.email})` : identity.email}
                <CloseButton className='user-close-btn' disabled={disabled} onClick={() => removeRecipient(index)} />
              </TagElement>
            ))}
          </Col>
        </Row>
      ) : null}
    </>
  )
}
